import * as React from 'react';
import { Image, tokens, makeStyles } from '@fluentui/react-components';
import SettingsPanel from './SettingsPanel';
import { useAppearance } from '../appearance';
import { AuthUser } from '../../services/auth';

export interface HeaderProps {
  title: string;
  /** Logo shown in light mode. */
  lightLogo: string;
  /** Logo shown in dark mode (white wordmark). */
  darkLogo: string;
  user: AuthUser | null;
  onAuthChange: (user: AuthUser | null) => void;
  settingsOpen: boolean;
  onSettingsOpenChange: (open: boolean) => void;
}

const useStyles = makeStyles({
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px 12px',
    backgroundColor: tokens.colorNeutralBackground3,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    gap: '12px',
  },
  left: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    flexShrink: 0,
  },
  logo: {
    height: '28px',
    width: 'auto',
    flexShrink: 0,
  },
  user: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    minWidth: 0,
  },
  userName: {
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase200,
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  userEmail: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground3,
    lineHeight: tokens.lineHeightBase100,
    maxWidth: '180px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const Header: React.FC<HeaderProps> = ({
  title,
  lightLogo,
  darkLogo,
  user,
  onAuthChange,
  settingsOpen,
  onSettingsOpenChange,
}) => {
  const styles = useStyles();
  const { isDark } = useAppearance();
  const logoSrc = isDark ? darkLogo : lightLogo;
  const displayName = user ? `${user.firstName}${user.lastName ? ` ${user.lastName}` : ''}` : null;
  return (
    <header className={styles.header}>
      <div className={styles.left}>
        <Image className={styles.logo} src={logoSrc} alt={title} />
        <SettingsPanel
          user={user}
          onAuthChange={onAuthChange}
          open={settingsOpen}
          onOpenChange={onSettingsOpenChange}
        />
      </div>
      <div className={styles.user}>
        {displayName && <span className={styles.userName}>{displayName}</span>}
        {user && <span className={styles.userEmail}>{user.email}</span>}
      </div>
    </header>
  );
};

export default Header;
